/**
 * Users Page - with Server-Side Pagination, Sorting and Quick Filters
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Plus, UserCog, Edit, Trash2, Shield, User, Download, FileText, FileSpreadsheet, ChevronDown, CheckSquare } from 'lucide-react';

import UserModal from './UserModal';
import './styles.css';

const ROLE_LABELS = {
  admin: 'Administrador',
  employee: 'Funcionário',
  waiter: 'Garçom',
  'admin-master': 'Admin Master',
};

const ROLE_COLORS = {
  admin: 'bg-purple-500/10 text-purple-500',
  employee: 'bg-blue-500/10 text-blue-500',
  waiter: 'bg-green-500/10 text-green-500',
  'admin-master': 'bg-red-500/10 text-red-500',
};

export default function Users() {
  const { t } = useTranslation();
  const { formatDate } = useInstance();
  const gridRef = useRef(null);

  // Data state
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Server-side pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({});

  // Selection state
  const [selectedCount, setSelectedCount] = useState(0);

  // Quick filter state
  const [quickFilter, setQuickFilter] = useState(null);

  // Load users with server-side params
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters object
      const apiFilters = {};
      if (filters.name) apiFilters.name = filters.name;
      if (filters.role && filters.role !== 'all') apiFilters.role = filters.role;
      if (filters.status && filters.status !== 'all') apiFilters.is_active = filters.status === 'active';

      // Apply quick filter - override status filter
      if (quickFilter === 'inactive') {
        apiFilters.is_active = false;
        delete apiFilters.role; // Clear role filter when filtering inactive
      }
      if (quickFilter === 'admin') {
        apiFilters.role = 'admin';
        delete apiFilters.is_active; // Show all admin regardless of status
      }
      if (quickFilter === 'waiter') {
        apiFilters.role = 'waiter';
        delete apiFilters.is_active; // Show all waiters regardless of status
      }

      const response = await api.post('/users/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
      });

      if (response.data?.success) {
        setUsers(response.data.data?.items || []);
        setTotalItems(response.data.data?.total || 0);
      } else {
        setUsers(response.data?.data?.items || response.data || []);
        setTotalItems(response.data?.data?.total || response.data?.length || 0);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      // Fallback to old endpoint
      try {
        const res = await api.get('/users');
        setUsers(res.data || []);
        setTotalItems(res.data?.length || 0);
      } catch (e) {
        toast.error(t('errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters, quickFilter, t]);

  // Load profiles
  const loadProfiles = useCallback(async () => {
    try {
      const res = await api.get('/profiles');
      setProfiles(res.data || []);
    } catch (err) {
      console.error('Error loading profiles:', err);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Handle page change from grid
  const handlePageChange = useCallback((newPage, newPageSize) => {
    setPageNumber(newPage);
    setPageSize(newPageSize);
  }, []);

  // Handle sort change from grid
  const handleSortChange = useCallback((newSortBy, newSortDirection) => {
    if (newSortBy && newSortDirection) {
      setSortBy(newSortBy);
      setSortDirection(newSortDirection);
    }
  }, []);

  // Handle filter search
  const handleSearch = useCallback(() => {
    setPageNumber(1);
    loadUsers();
  }, [loadUsers]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setQuickFilter(null);
    setPageNumber(1);
  }, []);

  // Quick filter handlers
  const handleQuickFilter = useCallback((filter) => {
    setQuickFilter(prev => prev === filter ? null : filter);
    setPageNumber(1);
  }, []);

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingUser?.id) {
        await api.put(`/users/${editingUser.id}`, data);
      } else {
        await api.post('/users', data);
      }
      toast.success(t('users.saveSuccess'));
      setModalOpen(false);
      setEditingUser(null);
      await loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingUser to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingUser(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success(t('users.deleteSuccess'));
      setDeleteId(null);
      await loadUsers();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  // Export handlers
  const handleExportCSV = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    gridRef.current?.exportCSV(onlySelected);
  }, [selectedCount]);

  const handleExportJSON = useCallback((onlySelected = false) => {
    if (onlySelected && selectedCount === 0) {
      toast.error('Selecione pelo menos um item para exportar');
      return;
    }
    const data = gridRef.current?.exportJSON(onlySelected);
    if (!data?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usuarios_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedCount]);

  // Track selection changes
  const handleSelectionChanged = useCallback(() => {
    const count = gridRef.current?.getSelectedCount() || 0;
    setSelectedCount(count);
  }, []);

  const filterDefinitions = useMemo(() => [
    {
      key: 'name',
      label: 'Nome/Email',
      type: 'text',
      placeholder: 'Buscar usuário...',
    },
    {
      key: 'role',
      label: 'Cargo',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'admin', label: 'Administrador' },
        { value: 'employee', label: 'Funcionário' },
        { value: 'waiter', label: 'Garçom' },
      ],
    },
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'active', label: 'Ativo' },
        { value: 'inactive', label: 'Inativo' },
      ],
    },
  ], [t]);

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: t('users.name'),
      flex: 2,
      sortable: true,
      cellRenderer: (params) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="font-medium">{params.value}</p>
            <p className="text-xs text-muted-foreground">{params.data.email}</p>
          </div>
        </div>
      ),
    },
    {
      field: 'role',
      headerName: t('users.role'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[params.value] || 'bg-muted'}`}>
          <Shield className="h-3 w-3" />
          {ROLE_LABELS[params.value] || params.value}
        </span>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Cadastro',
      flex: 1,
      sortable: true,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'is_active',
      headerName: t('common.status'),
      flex: 0.8,
      sortable: true,
      cellRenderer: (params) => (
        <span className={params.value ? 'datagrid-status-active' : 'datagrid-status-inactive'}>
          {params.value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      flex: 0.8,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <div className="datagrid-actions">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setEditingUser(params.data);
              setModalOpen(true);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleteId(params.data.id)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ], [t, formatDate]);

  // Quick filters
  const quickFilters = [
    { key: 'admin', label: 'Administradores', icon: Shield, color: 'purple' },
    { key: 'waiter', label: 'Garçons', icon: User, color: 'green' },
    { key: 'inactive', label: 'Inativos', icon: User, color: 'red' },
  ];

  // Export actions dropdown
  const ExportActions = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Exportar
          {selectedCount > 0 && (
            <Badge variant="secondary" className="ml-2">{selectedCount}</Badge>
          )}
          <ChevronDown className="h-3 w-3 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExportCSV(false)}>
          <FileText className="h-4 w-4 mr-2" />
          Exportar Tudo (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExportJSON(false)}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Exportar Tudo (JSON)
        </DropdownMenuItem>
        {selectedCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleExportCSV(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExportJSON(true)}>
              <CheckSquare className="h-4 w-4 mr-2" />
              Exportar {selectedCount} Selecionados (JSON)
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="users-page" data-testid="users-page">
      <PageHeader
        title={t('users.title')}
        icon={UserCog}
        subtitle={`${totalItems} usuários cadastrados`}
        actions={
          <div className="flex items-center gap-2">
            <ExportActions />
            <Button onClick={() => { setEditingUser(null); setModalOpen(true); }} data-testid="new-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t('users.newUser')}
            </Button>
          </div>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={setFilters}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
        showExport={false}
      />

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {quickFilters.map(qf => {
          const Icon = qf.icon;
          const isActive = quickFilter === qf.key;
          return (
            <Button
              key={qf.key}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleQuickFilter(qf.key)}
              className={isActive ? '' : `hover:bg-${qf.color}-500/10`}
              data-testid={`quick-filter-${qf.key}`}
            >
              <Icon className="h-4 w-4 mr-1" />
              {qf.label}
            </Button>
          );
        })}
        {quickFilter && (
          <Button variant="ghost" size="sm" onClick={() => setQuickFilter(null)}>
            Limpar filtro
          </Button>
        )}
      </div>

      <DataGrid
        ref={gridRef}
        title="Lista de Usuários"
        columnDefs={columnDefs}
        rowData={users}
        loading={loading}
        enableSelection={true}
        getRowId={(params) => params.data.id}
        serverSide={true}
        totalItems={totalItems}
        pageNumber={pageNumber}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortDirection={sortDirection}
        paginationPageSize={pageSize}
        onSelectionChanged={handleSelectionChanged}
      />

      <UserModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        user={editingUser}
        profiles={profiles}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('users.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
